import { useQuery } from "@tanstack/react-query"
import { useSearchParams } from "react-router-dom"
import { IGetReportSummary } from "../typings"
import { Spin } from "antd"
import { DocumentArrowDownIcon } from "@heroicons/react/24/outline"
import { useAuthStore } from "stores"
import { formatCurrencyNoSymbol } from "utils/currency"
import { mixPanelEvent } from "components/new-stuff/mix-panel/MixPanel"
import { useRef } from "react"
import { PAYROLL_ENDPOINTS, payrollService } from "services/payrollService"
import DashboardWrapper from "components/new-stuff/ui/DashboardWrapper"
import { getHireDateTime } from "./PayRollReportDetails"
import EnhancedReportTable from "components/new-stuff/report-table/EnhancedReportTable"
import { exportTablesToXlsx } from "pages/dashboard/documents/reports/utils/export-xlsx"
import { SkeletonLines } from "components/new-stuff/ui/Skeleton"

type IReport = IBaseRes<IGetReportSummary>

const PayRollReportVariance = () => {
  const [searchParams] = useSearchParams()
  const id = searchParams.get("id")
  const { user } = useAuthStore()
  const ref = useRef<HTMLDivElement>(null)

  const { isLoading: isGetPayrollSummaryLoading, data } = useQuery<IReport>({
    queryKey: [PAYROLL_ENDPOINTS.VIEW_REPORT_VARIANCE, id],
    queryFn: async () => {
      return await payrollService.viewReportVariance(id || "")
    },
    onSuccess() {
      mixPanelEvent("payroll-report-variance-listing")
    },
  })

  if (isGetPayrollSummaryLoading) {
    return (
      <DashboardWrapper>
        <div className="flex h-20 flex flex-col gap-3 p-4" aria-busy="true">
          <SkeletonLines lines={5} />
        </div>
      </DashboardWrapper>
    )
  }

  const payrollSummary = data?.data

  const allowancesData =
    payrollSummary?.payrollReportSummaryAllowances
      ?.map((item) => {
        const baseKey = buildAllowanceBaseKey(item.description, item.id)
        const currentField = buildAllowanceFieldKey(
          item.description,
          item.id,
          "current"
        )
        const previousField = buildAllowanceFieldKey(
          item.description,
          item.id,
          "previous"
        )

        return {
          title: item.description,
          key: baseKey,
          children: [
            {
              title: "Current",
              dataIndex: currentField,
              key: `${baseKey}-current`,
              render: renderNeutralCurrency(currentField, { hideEmpty: true }),
            },
            {
              title: "Previous",
              dataIndex: previousField,
              key: `${baseKey}-previous`,
              render: renderNeutralCurrency(previousField, { hideEmpty: true }),
            },
            {
              title: "Variance",
              key: `${baseKey}-variance`,
              render: renderVarianceCurrency(currentField, previousField),
            },
          ],
        }
      })
      .sort((a, b) => {
        if (a.title.toLowerCase() === "consolidated salary") return -1
        if (b.title.toLowerCase() === "consolidated salary") return 1
        return a.title.localeCompare(b.title)
      }) || []

  const trueBenefits = filterPropertiesWithTrue(
    payrollSummary && payrollSummary?.payrollReportSummaryValues
      ? payrollSummary?.payrollReportSummaryValues[0]
      : {}
  )

  const benefitsData: {
    title: string
    dataIndex: string
    key: string
    render: (text: any, record: any) => JSX.Element
  }[] = []

  const payrollSummaryValues = payrollSummary?.payrollReportSummaryValues

  Object.keys(trueBenefits).forEach((key) => {
    const dataIndex = key.replace("has_", "")
    const title = generateTitle(dataIndex)

    payrollSummaryValues?.forEach((item) => {
      benefitsData.push({
        title,
        dataIndex,
        key: dataIndex,
        render: (text, record) => {
          return record[dataIndex] ? (
            <span className={"font-avenir text-neutral500 whitespace-nowrap"}>
              {record.currency_symbol}{" "}
              {formatCurrencyNoSymbol(Number(record[dataIndex] || ""))}
            </span>
          ) : (
            <></>
          )
        },
      })
    })
  })

  const TableColumns = [
    {
      title: "Employee Name",
      dataIndex: "full_name",
      key: "full_name",
      width: 200,
    },
    {
      title: "Employee Number",
      dataIndex: "identification_number",
      key: "identification_number",
    },
    {
      title: "Role",
      dataIndex: "employee_title",
      key: "employee_title",
    },
    {
      title: "Hire Date",
      dataIndex: "hire_date",
      key: "hire_date",
    },
    {
      title: "Department",
      dataIndex: "department",
      key: "department",
    },
    {
      title: "Workplace",
      dataIndex: "office_display_name",
      key: "office_display_name",
    },
    {
      title: "Gender",
      dataIndex: "gender",
      key: "gender",
    },
    {
      title: "Paygrade",
      dataIndex: "paygrade",
      key: "paygrade",
    },
    ...allowancesData,
    {
      title: "Other Payables",
      children: [
        {
          title: "Current",
          dataIndex: "payable",
          key: "payable",
          render: renderNeutralCurrency("payable"),
        },
        {
          title: "Previous",
          dataIndex: "last_payable",
          key: "last_payable",
          render: renderNeutralCurrency("last_payable"),
        },
        {
          title: "Variance",
          key: "payable_variance",
          render: renderVarianceCurrency("payable", "last_payable"),
        },
      ],
    },
    {
      title: "Gross Pay",
      children: [
        {
          title: "Current",
          dataIndex: "gross",
          key: "gross",
          render: renderNeutralCurrency("gross"),
        },
        {
          title: "Previous",
          dataIndex: "last_gross",
          key: "last_gross",
          render: renderNeutralCurrency("last_gross"),
        },
        {
          title: "Variance",
          key: "gross_variance",
          render: renderVarianceCurrency("gross", "last_gross"),
        },
      ],
    },
    {
      title: "Tax",
      children: [
        {
          title: "Current",
          dataIndex: "tax",
          key: "tax",
          render: renderNeutralCurrency("tax"),
        },
        {
          title: "Previous",
          dataIndex: "last_tax",
          key: "last_tax",
          render: renderNeutralCurrency("last_tax"),
        },
        {
          title: "Variance",
          key: "tax_variance",
          render: renderVarianceCurrency("tax", "last_tax"),
        },
      ],
    },
    ...benefitsData,
    {
      title: "Total Deductions",
      children: [
        {
          title: "Current",
          dataIndex: "deduction",
          key: "deduction",
          render: renderNeutralCurrency("deduction"),
        },
        {
          title: "Previous",
          dataIndex: "last_deduction",
          key: "last_deduction",
          render: renderNeutralCurrency("last_deduction"),
        },
        {
          title: "Variance",
          key: "deduction_variance",
          render: renderVarianceCurrency("deduction", "last_deduction"),
        },
      ],
    },
    {
      title: "Net Pay",
      children: [
        {
          title: "Current",
          dataIndex: "net",
          key: "net",
          render: renderNeutralCurrency("net"),
        },
        {
          title: "Previous",
          dataIndex: "last_net",
          key: "last_net",
          render: renderNeutralCurrency("last_net"),
        },
        {
          title: "Variance",
          key: "net_variance",
          render: renderVarianceCurrency("net", "last_net"),
        },
      ],
    },
    {
      title: "Days Worked",
      dataIndex: "days_worked",
      key: "days_worked",
      render: (text: any, record: any) => {
        return (
          <span className={"font-avenir text-neutral500 whitespace-nowrap"}>
            {record.days_worked}/{record.total_days}
          </span>
        )
      },
      width: 120,
    },
  ]

  const TableData =
    payrollSummary?.payrollReportSummaries
      ?.sort(
        (a, b) => getHireDateTime(a.hire_date) - getHireDateTime(b.hire_date)
      )
      ?.map((summary) => {
        const rowData: any = { ...summary }
        payrollSummary?.payrollReportSummaryAllowances?.forEach((allowance) => {
          const currentField = buildAllowanceFieldKey(
            allowance.description,
            allowance.id,
            "current"
          )
          const previousField = buildAllowanceFieldKey(
            allowance.description,
            allowance.id,
            "previous"
          )
          const earning = payrollSummary?.payrollReportSummaryEarnings?.find(
            (earning) =>
              earning.person_id === summary.person_id &&
              earning.description === allowance.description
          )
          rowData[currentField] = earning ? earning.amount : null
          rowData[previousField] = earning ? earning.last_amount : null
        })
        return rowData
      }) || []

  return (
    <DashboardWrapper>
      <div className="d">
        <div className="">
          {/* ========Download Button======== */}
          {/* ========Download Button======== */}

          {/* ========Table Header======== */}
          {/* ========Table Header======== */}
          <div className=" px-6">
            <div className="flex justify-between items-center gap-4">
              <h1 className="text-xl font-circular text-neutral600 font-medium">
                Payroll Variance Report
                {payrollSummary?.payrollStatusId &&
                payrollSummary?.payrollStatusId < 4
                  ? "(Draft)"
                  : ""}
              </h1>
              <button
                className="px-4 py-2 font-avenir !text-sm text-a11y rounded-md bg-primary500 flex items-center"
                onClick={async () => {
                  // exportTableToCSV(
                  //   `${payrollSummary?.report_download_title}.csv`,
                  //   `
                  // ${payrollSummary?.qualifiedPayrollDate} Payroll. Period: ${payrollSummary?.payPeriod}
                  // `
                  // )
                  const sheet =
                    payrollSummary?.payrollStatusId &&
                    payrollSummary?.payrollStatusId < 4
                      ? `Payroll Variance Report (Draft)`
                      : `Payroll Variance Report`

                  exportTablesToXlsx(
                    ref.current,
                    `${payrollSummary?.report_download_title}.xlsx`,
                    {
                      sheetName: sheet,
                    }
                  )
                }}
              >
                <DocumentArrowDownIcon className="w-5 h-5 mr-1" />
                <span>Excel</span>
              </button>
            </div>
            <hr className="my-4" />

            <span className="text-neutral500 font-avenir ">
              {" "}
              Report created on {payrollSummary?.report_date}{" "}
            </span>
          </div>
          <div className="flex flex-col items-end my-4 px-6 text-neutral500 text-sm font-avenir">
            <span> {user?.userOrganisation} </span>
            <span> {payrollSummary?.officeAddress?.address_line_1} </span>
            <span>
              {" "}
              {payrollSummary?.officeAddress?.address_city}{" "}
              {payrollSummary?.officeAddress?.address_state}{" "}
              {payrollSummary?.officeAddress?.zip_code}{" "}
              {payrollSummary?.officeAddress?.country}{" "}
            </span>
          </div>
          <div className=" my-4 px-6 text-neutral500 text-sm font-avenir">
            <span className="font-semibold">
              {payrollSummary?.qualifiedPayrollDate} Payroll.
            </span>{" "}
            Period: {payrollSummary?.payPeriod}
          </div>

          <div
            className="px-6"
            ref={ref}
          >
            {/* =======Table====== */}
            {/* =======Table====== */}
            <EnhancedReportTable
              loading={isGetPayrollSummaryLoading}
              dataSource={TableData}
              columns={TableColumns}
              rowKey="person_id"
              defaultFreezeBoundaryKey="full_name"
              scroll={{
                y: "calc(100vh - 300px)",
                x: "max-content",
              }}
              tableLayout="auto"
              bordered
              className="[&_thead_*]:!font-avenir [&_tbody_*]:!font-avenir [&_tbody_*]:text-neutral500"
            />
          </div>
        </div>
      </div>
    </DashboardWrapper>
  )
}

export default PayRollReportVariance

// Helpers to reuse consistent currency formatting and variance styling.
const normalizeAmount = (value: unknown) => {
  const parsed = Number(value || "")
  return Number.isNaN(parsed) ? 0 : parsed
}

const buildAllowanceBaseKey = (description: string, id: number) => {
  const normalized = description
    ?.toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")

  return `${normalized || "allowance"}_${id}`
}

const buildAllowanceFieldKey = (
  description: string,
  id: number,
  type: "current" | "previous"
) => `${buildAllowanceBaseKey(description, id)}_${type}`

const renderNeutralCurrency =
  (field: string, options?: { hideEmpty?: boolean }) =>
  (_text: any, record: Record<string, any>) => {
    const value = record?.[field]

    if (options?.hideEmpty && (value === null || value === undefined)) {
      return ""
    }

    return (
      <span className="font-avenir text-neutral500 whitespace-nowrap">
        {record.currency_symbol}{" "}
        {formatCurrencyNoSymbol(normalizeAmount(value))}
      </span>
    )
  }

const renderVarianceCurrency =
  (currentField: string, previousField: string) =>
  (_text: any, record: Record<string, any>) => {
    const current = normalizeAmount(record?.[currentField])
    const previous = normalizeAmount(record?.[previousField])
    const diff = current - previous
    const diffClass =
      diff > 0
        ? "!text-success500"
        : diff < 0
        ? "!text-red-500"
        : "text-neutral500"
    const formatted = formatCurrencyNoSymbol(Math.abs(diff))
    const symbol = record.currency_symbol || ""

    // An arrow reads as direction at a glance where a leading +/- is easily
    // mistaken for part of the figure. The colour already carries the same
    // meaning, so the arrow is hidden from screen readers and the direction is
    // given in words instead.
    const direction = diff > 0 ? "up" : diff < 0 ? "down" : null

    return (
      <span className={`font-avenir whitespace-nowrap ${diffClass}`}>
        {direction && (
          <>
            <span aria-hidden="true">{direction === "up" ? "↑" : "↓"}</span>
            <span className="sr-only">
              {direction === "up" ? "increase of" : "decrease of"}
            </span>{" "}
          </>
        )}
        {`${symbol} ${formatted}`}
      </span>
    )
  }

const generateTitle = (dataIndex: string) => {
  let title = dataIndex

  if (dataIndex.includes("deduction")) {
    title = title
      .replace("_deduction", " Employee")
      .replace("assurance", "Assurance")
      .replace("health_insurance", "NHIS")
  } else if (dataIndex.includes("contribution")) {
    title = title
      .replace("_contribution", " Employer")
      .replace("assurance", "Assurance")
      .replace("health_insurance", "NHIS")
  }

  // Capitalize the first letter of the title
  title = title.replace(/^\w/, (c) => c.toUpperCase())

  return title
    .replace(/_/g, " ")
    .replace("Benefit", "Benefits")
    ?.replace("hmo", "HMO")
    ?.replace("Other Employee", "Other Deductions")
}

function filterPropertiesWithTrue(obj: { [key: string]: any }): {
  [key: string]: boolean
} {
  return Object.keys(obj).reduce((acc, key) => {
    if (typeof obj[key] === "boolean" && obj[key] === true) {
      acc[key] = obj[key]
    }
    return acc
  }, {} as { [key: string]: boolean })
}

function downloadCSV(csv: any, filename: any) {
  var csvFile
  var downloadLink
  // CSV file
  csvFile = new Blob([csv], { type: "text/csv" })
  //  Download link
  downloadLink = document.createElement("a")
  downloadLink.download = filename
  downloadLink.href = window.URL.createObjectURL(csvFile)
  downloadLink.style.display = "none"
  document.body.appendChild(downloadLink)
  downloadLink.click()
}

function exportTableToCSV(filename: any, header: string) {
  var csv = []
  // add the header row
  csv.push(header)

  // get all table rows
  var rows = document.querySelectorAll("table tr")
  // loop over each table row and populate array
  for (var i = 0; i < rows.length; i++) {
    var row = [],
      //  get each column in the row
      cols = rows[i].querySelectorAll("td, th")
    for (var j = 0; j < cols.length; j++) {
      // check if column is a div and if so , remove it
      if (cols[j].querySelector("div")) {
        //  remove div
        cols[j].querySelector("div")?.remove()
      } else if (cols[j].querySelector("span")) {
        row.push(
          cols[j]
            ?.querySelector("span")
            ?.innerHTML.replace(/,/g, "")
            .replace(/₦/g, "")
        )
      } else row.push(cols[j].innerHTML.replace(/,/g, "").replace(/₦/g, "")) //  push column text into array
    }
    // join each row with a comma
    csv.push(row.join(","))
  }

  downloadCSV(csv.join("\n"), filename)
}
